import { Title, Text, Card, Grid, Metric, Flex, Badge } from '@tremor/react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/context/AuthContext'
import { getMachinesWithMetrics } from '@/api/machines'
import type { MachineWithMetrics } from '@/types'
import {
  ExclamationTriangleIcon,
  MicrophoneIcon,
  BoltIcon,
  WifiIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline'
import { TelephonyTestWidget } from '@/components/telephony/TelephonyTestWidget'

interface Alert {
  machine: string
  type: 'mic_muted' | 'low_battery' | 'high_ping' | 'device_mismatch'
  message: string
  severity: 'warning' | 'error'
}

export function Dashboard() {
  const { user } = useAuth()

  const { data: machines = [], isLoading } = useQuery({
    queryKey: ['machines-with-metrics'],
    queryFn: getMachinesWithMetrics,
    refetchInterval: 10000,
  })

  // Calculate statistics
  const onlineCount = machines.filter((m: MachineWithMetrics) => m.status === 'online').length
  const offlineCount = machines.filter((m: MachineWithMetrics) => m.status === 'offline').length
  const totalCount = machines.length

  // Calculate average CPU and RAM for online machines
  const onlineMachines = machines.filter((m: MachineWithMetrics) => m.status === 'online' && m.metrics)
  const avgCpu = onlineMachines.length > 0
    ? Math.round(onlineMachines.reduce((sum: number, m: MachineWithMetrics) => sum + (m.metrics?.cpu_percent || 0), 0) / onlineMachines.length)
    : null
  const avgRam = onlineMachines.length > 0
    ? Math.round(onlineMachines.reduce((sum: number, m: MachineWithMetrics) => sum + (m.metrics?.ram_percent || 0), 0) / onlineMachines.length)
    : null

  // Build alerts list
  const alerts: Alert[] = []
  machines.forEach((m: MachineWithMetrics) => {
    if (!m.metrics || m.status !== 'online') return
    const name = m.operator_name || m.name

    if (m.metrics.mic_muted) {
      alerts.push({
        machine: name,
        type: 'mic_muted',
        message: 'Microphone en sourdine',
        severity: 'error'
      })
    }
    if (m.metrics.battery_percent !== null && !m.metrics.battery_plugged && m.metrics.battery_percent < 20) {
      alerts.push({
        machine: name,
        type: 'low_battery',
        message: `Batterie faible (${m.metrics.battery_percent}%)`,
        severity: 'warning'
      })
    }
    if (m.metrics.ping_ms !== null && m.metrics.ping_ms > 150) {
      alerts.push({
        machine: name,
        type: 'high_ping',
        message: `Latence élevée (${Math.round(m.metrics.ping_ms)}ms)`,
        severity: 'warning'
      })
    }
    if (m.metrics.input_device_match === false || m.metrics.output_device_match === false) {
      alerts.push({
        machine: name,
        type: 'device_mismatch',
        message: 'Mauvais périphérique audio',
        severity: 'warning'
      })
    }
  })
  const alertCount = alerts.length

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <Title>Bienvenue, {user?.fullname}</Title>
        <Text>Vue d'ensemble du système SONAR</Text>
      </div>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-3 sm:gap-6 mb-4 sm:mb-6">
        <Card decoration="top" decorationColor="green">
          <Text>Opérateurs en ligne</Text>
          <Metric>{isLoading ? '...' : onlineCount}</Metric>
          <Flex className="mt-2 gap-2">
            <Badge color="green">{onlineCount} en ligne</Badge>
            {offlineCount > 0 && <Badge color="gray">{offlineCount} hors ligne</Badge>}
          </Flex>
        </Card>

        <Card decoration="top" decorationColor={alertCount > 0 ? 'yellow' : 'green'}>
          <Text>Alertes actives</Text>
          <Metric>{isLoading ? '...' : alertCount}</Metric>
          <Flex className="mt-2">
            <Badge color={alertCount > 0 ? 'yellow' : 'green'}>
              {alertCount > 0 ? `${alertCount} alertes` : 'Aucune alerte'}
            </Badge>
          </Flex>
        </Card>

        <Card decoration="top" decorationColor={avgCpu !== null && avgCpu > 70 ? 'yellow' : 'blue'}>
          <Text>CPU moyen</Text>
          <Metric>{isLoading ? '...' : avgCpu !== null ? `${avgCpu}%` : '--%'}</Metric>
          {avgCpu !== null && avgCpu > 70 && (
            <Text className="text-yellow-500 text-sm mt-1">Charge élevée</Text>
          )}
        </Card>

        <Card decoration="top" decorationColor={avgRam !== null && avgRam > 80 ? 'yellow' : 'blue'}>
          <Text>RAM moyenne</Text>
          <Metric>{isLoading ? '...' : avgRam !== null ? `${avgRam}%` : '--%'}</Metric>
          {avgRam !== null && avgRam > 80 && (
            <Text className="text-yellow-500 text-sm mt-1">Mémoire haute</Text>
          )}
        </Card>
      </Grid>

      <Grid numItems={1} numItemsMd={2} numItemsLg={3} className="gap-3 sm:gap-6">
        {/* Active Alerts */}
        <Card>
          <Flex justifyContent="between" className="mb-4">
            <Title className="text-lg">Alertes actives</Title>
            {alertCount > 0 && <Badge color="yellow">{alertCount}</Badge>}
          </Flex>
          {isLoading ? (
            <Text className="text-gray-500">Chargement...</Text>
          ) : alerts.length === 0 ? (
            <Flex justifyContent="start" className="gap-2 text-green-500">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <Text className="text-green-500">Aucune alerte - Tout est OK</Text>
            </Flex>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 5).map((alert, idx) => {
                const Icon = alert.type === 'mic_muted' ? MicrophoneIcon
                  : alert.type === 'low_battery' ? BoltIcon
                  : alert.type === 'high_ping' ? WifiIcon
                  : SpeakerWaveIcon
                return (
                  <Flex key={idx} justifyContent="start" className={`gap-3 p-2 rounded ${
                    alert.severity === 'error' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'
                  }`}>
                    <Icon className={`w-5 h-5 ${alert.severity === 'error' ? 'text-red-500' : 'text-yellow-500'}`} />
                    <div>
                      <Text className="font-medium text-sm">{alert.machine}</Text>
                      <Text className={`text-xs ${alert.severity === 'error' ? 'text-red-600' : 'text-yellow-600'}`}>
                        {alert.message}
                      </Text>
                    </div>
                  </Flex>
                )
              })}
              {alerts.length > 5 && (
                <Text className="text-sm text-gray-500 text-center pt-2">
                  Et {alerts.length - 5} autres alertes...
                </Text>
              )}
            </div>
          )}
        </Card>

        {/* Operators Summary */}
        <Card>
          <Title className="text-lg">Opérateurs</Title>
          {isLoading ? (
            <Text className="mt-4 text-gray-500">Chargement...</Text>
          ) : totalCount === 0 ? (
            <Text className="mt-4 text-gray-500">Aucun opérateur configuré</Text>
          ) : (
            <div className="mt-4 space-y-2">
              {machines.slice(0, 5).map((m: MachineWithMetrics) => (
                <div key={m.machine_id} className="p-3 rounded bg-gray-50 dark:bg-gray-800">
                  <Flex justifyContent="between" alignItems="center">
                    <Flex justifyContent="start" className="gap-3" alignItems="center">
                      <div className={`w-2 h-2 rounded-full ${m.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <div>
                        <Text className="font-medium">{m.operator_name || m.name}</Text>
                        <Text className="text-xs text-gray-500">{m.location}</Text>
                      </div>
                    </Flex>
                    {m.metrics && m.status === 'online' ? (
                      <Flex className="gap-4">
                        <Text className="text-xs text-gray-500">CPU <span className="font-medium text-gray-700 dark:text-gray-300">{m.metrics.cpu_percent}%</span></Text>
                        <Text className="text-xs text-gray-500">RAM <span className="font-medium text-gray-700 dark:text-gray-300">{m.metrics.ram_percent}%</span></Text>
                      </Flex>
                    ) : (
                      <Text className="text-xs text-gray-400">Hors ligne</Text>
                    )}
                  </Flex>
                </div>
              ))}
              {totalCount > 5 && (
                <Text className="text-sm text-gray-500 text-center pt-2">
                  Et {totalCount - 5} autres opérateurs...
                </Text>
              )}
            </div>
          )}
        </Card>

        {/* Telephony Test */}
        <TelephonyTestWidget />
      </Grid>
    </div>
  )
}
